"use client";

import { useEffect, useRef, useState } from "react";
import type { TocItem } from "@/lib/blog-content";

/**
 * Client-side reading enhancements for the editorial article template:
 *  - a scroll-driven reading-progress bar
 *  - a sticky desktop table of contents + a horizontal mobile pill strip
 *  - scroll-spy (IntersectionObserver) that highlights the active section
 *
 * Renders both TOC variants itself from the `toc` prop so the markup and the
 * behaviour live together. Returns nothing visible when `toc` is empty.
 */
export function ReadingProgress() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = ref.current;
    if (!bar) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const pct = max > 0 ? (window.scrollY / max) * 100 : 0;
      bar.style.width = `${Math.min(Math.max(pct, 0), 100)}%`;
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  return <div className="post__progress" ref={ref} aria-hidden="true" />;
}

export function TableOfContents({
  toc,
  headingLevel,
}: {
  toc: TocItem[];
  headingLevel: "h2" | "h3";
}) {
  const [activeId, setActiveId] = useState<string>(toc[0]?.id ?? "");
  const pillsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!toc.length) return;
    const ids = toc.map((t) => t.id);
    const headings = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (!headings.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]?.target.id) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-72px 0px -55% 0px", threshold: 0 }
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [toc, headingLevel]);

  // keep the active pill scrolled into view on mobile
  useEffect(() => {
    const strip = pillsRef.current;
    if (!strip || !activeId) return;
    const active = strip.querySelector<HTMLElement>(
      `[data-toc-id="${CSS.escape(activeId)}"]`
    );
    if (!active) return;
    const a = active.getBoundingClientRect();
    const s = strip.getBoundingClientRect();
    if (a.left < s.left || a.right > s.right) {
      active.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [activeId]);

  if (!toc.length) return null;

  const handleClick = (e: React.MouseEvent, id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    setActiveId(id);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    history.replaceState(null, "", `#${id}`);
  };

  return (
    <>
      {/* Desktop sticky TOC */}
      <aside className="post__toc" aria-label="Table of contents">
        <div className="post__toc-heading">In this article</div>
        <ul className="post__toc-list">
          {toc.map((item) => (
            <li
              key={item.id}
              className={item.id === activeId ? "is-active" : undefined}
            >
              <a href={`#${item.id}`} onClick={(e) => handleClick(e, item.id)}>
                {item.text}
              </a>
            </li>
          ))}
        </ul>
      </aside>

      {/* Mobile / tablet pill strip */}
      <div className="post__toc-pills" ref={pillsRef} aria-hidden="true">
        <div className="post__toc-pills-inner">
          {toc.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              data-toc-id={item.id}
              // The strip duplicates the desktop TOC above, so it is hidden
              // from screen readers — which means its links must leave the tab
              // order too, or they become unreachable focus stops.
              tabIndex={-1}
              className={`post__toc-pill${
                item.id === activeId ? " is-active" : ""
              }`}
              onClick={(e) => handleClick(e, item.id)}
            >
              {item.number} · {item.text}
            </a>
          ))}
        </div>
      </div>
    </>
  );
}
